/**
 * Extract EPUB page text clipped to the currently visible viewport.
 * Avoids CFI Range.toString() bleed that pulls in previous-column text.
 */

function rectsIntersect(a, b) {
  return (
    a.left < b.right &&
    a.right > b.left &&
    a.top < b.bottom &&
    a.bottom > b.top
  );
}

/**
 * Visible area of an iframe relative to the iframe's client coordinate system.
 */
function getVisibleRectInIframe(iframe, containerEl) {
  const iframeRect = iframe.getBoundingClientRect();
  const containerRect = containerEl
    ? containerEl.getBoundingClientRect()
    : iframeRect;

  const left = Math.max(iframeRect.left, containerRect.left);
  const top = Math.max(iframeRect.top, containerRect.top);
  const right = Math.min(iframeRect.right, containerRect.right);
  const bottom = Math.min(iframeRect.bottom, containerRect.bottom);

  if (right - left < 1 || bottom - top < 1) {
    return null;
  }

  // Slight inset to avoid column-edge bleed from subpixel mapping
  const inset = 1;
  return {
    left: left - iframeRect.left + inset,
    top: top - iframeRect.top + inset,
    right: right - iframeRect.left - inset,
    bottom: bottom - iframeRect.top - inset,
  };
}

function isSkippedElement(el, doc) {
  if (!el || el.nodeType !== 1) {
    return true;
  }
  if (el.closest("script, style, noscript, svg title")) {
    return true;
  }
  if (el.closest('[aria-hidden="true"]')) {
    return true;
  }
  const win = doc.defaultView;
  if (!win) {
    return false;
  }
  try {
    const style = win.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    ) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/**
 * Keep only characters whose client rect intersects the visible viewport.
 */
function visibleSubstring(doc, textNode, visibleRect) {
  const text = textNode.nodeValue || "";
  if (!text) {
    return "";
  }

  const range = doc.createRange();
  range.selectNodeContents(textNode);
  const nodeRects = Array.from(range.getClientRects());
  if (nodeRects.length === 0) {
    return "";
  }

  const anyVisible = nodeRects.some((r) => rectsIntersect(r, visibleRect));
  if (!anyVisible) {
    return "";
  }

  const allVisible = nodeRects.every(
    (r) =>
      r.left >= visibleRect.left - 0.5 &&
      r.right <= visibleRect.right + 0.5 &&
      r.top >= visibleRect.top - 0.5 &&
      r.bottom <= visibleRect.bottom + 0.5
  );
  if (allVisible) {
    return text;
  }

  // Partial visibility (e.g. text split across CSS columns): per-character clip
  let out = "";
  for (let i = 0; i < text.length; i++) {
    range.setStart(textNode, i);
    range.setEnd(textNode, i + 1);
    const rects = range.getClientRects();
    for (let j = 0; j < rects.length; j++) {
      if (rectsIntersect(rects[j], visibleRect)) {
        out += text[i];
        break;
      }
    }
  }
  return out;
}

function extractVisibleTextFromDoc(doc, visibleRect) {
  if (!doc?.body || !visibleRect) {
    return "";
  }

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !/\S/.test(node.nodeValue)) {
        return NodeFilter.FILTER_REJECT;
      }
      const parent = node.parentElement;
      if (!parent || isSkippedElement(parent, doc)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const parts = [];
  let node = walker.nextNode();
  while (node) {
    const chunk = visibleSubstring(doc, node, visibleRect);
    if (chunk) {
      parts.push(chunk);
    }
    node = walker.nextNode();
  }
  return parts.join("");
}

function resolveViewIframe(view) {
  if (view?.iframe) {
    return view.iframe;
  }
  if (view?.element?.querySelector) {
    return view.element.querySelector("iframe");
  }
  const doc = view?.document || view?.contents?.document;
  return doc?.defaultView?.frameElement || null;
}

function resolveViewDocument(view, iframe) {
  return (
    view?.document ||
    view?.contents?.document ||
    iframe?.contentDocument ||
    null
  );
}

/**
 * @param {object} rendition epub.js Rendition
 * @returns {string} visible page text, or "" on failure
 */
export function getEpubVisiblePageText(rendition) {
  try {
    if (!rendition) {
      return "";
    }

    const manager = rendition.manager;
    const container = manager?.container || null;
    const views =
      typeof manager?.visible === "function" ? manager.visible() : [];

    const parts = [];

    if (views && views.length > 0) {
      for (let i = 0; i < views.length; i++) {
        const view = views[i];
        const iframe = resolveViewIframe(view);
        const doc = resolveViewDocument(view, iframe);
        if (!iframe || !doc?.body) {
          continue;
        }
        const visibleRect = getVisibleRectInIframe(iframe, container);
        if (!visibleRect) {
          continue;
        }
        const text = extractVisibleTextFromDoc(doc, visibleRect);
        if (text) {
          parts.push(text);
        }
      }
    } else if (typeof rendition.getContents === "function") {
      const contentsList = rendition.getContents() || [];
      for (let i = 0; i < contentsList.length; i++) {
        const content = contentsList[i];
        const doc = content?.document;
        const iframe =
          doc?.defaultView?.frameElement ||
          content?.content?.ownerDocument?.defaultView?.frameElement;
        if (!doc?.body) {
          continue;
        }
        const visibleRect = iframe
          ? getVisibleRectInIframe(iframe, container)
          : {
              left: 0,
              top: 0,
              right: doc.documentElement?.clientWidth || 0,
              bottom: doc.documentElement?.clientHeight || 0,
            };
        if (!visibleRect || visibleRect.right <= visibleRect.left) {
          continue;
        }
        const text = extractVisibleTextFromDoc(doc, visibleRect);
        if (text) {
          parts.push(text);
        }
      }
    }

    return parts.join("");
  } catch (err) {
    console.warn("getEpubVisiblePageText failed", err);
    return "";
  }
}

/**
 * Wait two animation frames so epub.js reportLocation / column layout settle.
 */
export function waitForEpubLayoutSettle() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}
